import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, ShieldCheck, Wrench, Award, ArrowRight, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";
import { SEO } from "@/components/SEO";
import { ReviewsSection } from "@/components/ReviewsSection";
import { api } from "@/lib/api";

export default function HomePage() {
    const [cars, setCars] = useState([]);
    const [merken, setMerken] = useState([]);
    const [filter, setFilter] = useState({ merk: "", max_prijs: "", brandstof: "" });

    useEffect(() => {
        api.get("/cars?limit=6&sort=nieuwste").then((r) => setCars(r.data)).catch(() => {});
        api.get("/cars/facets").then((r) => setMerken(r.data.merken || [])).catch(() => {});
    }, []);

    const buildSearchQS = () => {
        const p = new URLSearchParams();
        if (filter.merk) p.set("merk", filter.merk);
        if (filter.max_prijs) p.set("max_prijs", filter.max_prijs);
        if (filter.brandstof) p.set("brandstof", filter.brandstof);
        return p.toString();
    };

    return (
        <div className="App">
            <SEO description="Used Car Selection — geselecteerde tweedehands wagens met garantie in Bornem (België). Bekijk ons aanbod, verkoop uw wagen of plan een proefrit." />
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0E2C4D] text-white">
                <div className="absolute inset-0 opacity-30">
                    <img src="https://images.unsplash.com/photo-1777175013535-d10633cdb9ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                         alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0E2C4D] via-[#0E2C4D]/85 to-transparent" />
                </div>
                <div className="ucs-container relative py-24 md:py-36 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 fade-up">
                        <div className="inline-flex items-center gap-2 bg-[#EC6A0A]/20 text-[#EC6A0A] px-3 py-1.5 mb-6 text-xs tracking-[0.2em] font-bold uppercase">
                            <Sparkles className="w-3.5 h-3.5" /> Geselecteerde tweedehands wagens
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
                            Vind jouw <span className="text-[#EC6A0A]">perfecte</span> auto.
                            <br />Eerlijk, snel, online.
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
                            Ruim aanbod gekeurde tweedehands wagens met garantie. Reserveer online en ruil eenvoudig uw huidige wagen in.
                        </p>

                        <div className="mt-10 bg-white p-3 grid grid-cols-1 md:grid-cols-4 gap-3 max-w-3xl" style={{borderRadius: 2}} data-testid="hero-search-bar">
                            <select value={filter.merk} onChange={(e) => setFilter({ ...filter, merk: e.target.value })}
                                    className="px-4 py-3 text-sm text-[#0E2C4D] border border-slate-200 focus:outline-none focus:border-[#EC6A0A]"
                                    style={{borderRadius: 2}} data-testid="hero-filter-merk">
                                <option value="">Alle merken</option>
                                {merken.map((m) => <option key={m.merk} value={m.merk}>{m.merk} ({m.count})</option>)}
                            </select>
                            <select value={filter.brandstof} onChange={(e) => setFilter({ ...filter, brandstof: e.target.value })}
                                    className="px-4 py-3 text-sm text-[#0E2C4D] border border-slate-200 focus:outline-none focus:border-[#EC6A0A]"
                                    style={{borderRadius: 2}} data-testid="hero-filter-brandstof">
                                <option value="">Alle brandstoffen</option>
                                <option>Benzine</option><option>Diesel</option><option>Elektrisch</option><option>Hybride</option><option>LPG</option>
                            </select>
                            <select value={filter.max_prijs} onChange={(e) => setFilter({ ...filter, max_prijs: e.target.value })}
                                    className="px-4 py-3 text-sm text-[#0E2C4D] border border-slate-200 focus:outline-none focus:border-[#EC6A0A]"
                                    style={{borderRadius: 2}} data-testid="hero-filter-prijs">
                                <option value="">Max. prijs</option>
                                <option value="10000">Tot € 10.000</option>
                                <option value="20000">Tot € 20.000</option>
                                <option value="35000">Tot € 35.000</option>
                                <option value="60000">Tot € 60.000</option>
                            </select>
                            <Link to={`/aanbod?${buildSearchQS()}`} className="ucs-btn-accent justify-center" data-testid="hero-search-cta">
                                <Search className="w-4 h-4" /> Zoeken
                            </Link>
                        </div>
                    </div>

                    <div className="hidden md:block md:col-span-5">
                        <div className="grid grid-cols-2 gap-6 fade-up delay-200">
                            <Stat value="500+" label="Verkocht in 2025" />
                            <Stat value="4,8★" label="Klantbeoordeling" />
                            <Stat value="12 mnd" label="Garantie standaard" />
                            <Stat value="24 u" label="Reactie gegarandeerd" />
                        </div>
                    </div>
                </div>
            </section>

            {/* USP */}
            <section className="py-20 bg-white border-b border-slate-200">
                <div className="ucs-container grid md:grid-cols-3 gap-10">
                    <USP icon={<ShieldCheck />} title="Zorgvuldig nagekeken"
                         desc="Elke wagen wordt door onze monteurs grondig nagekeken en opgekuist vóór levering." />
                    <USP icon={<Award />} title="Persoonlijk advies"
                         desc="Ons team neemt de tijd om u te helpen — eerlijk en zonder druk." />
                    <USP icon={<Wrench />} title="12 maanden wettelijke garantie"
                         desc="Standaard 12 maanden wettelijke garantie op elke verkochte wagen." />
                </div>
            </section>

            {/* CTA VERKOOP — prominent, eerst zichtbaar voor advertenties */}
            <section className="py-24 bg-[#0E2C4D] text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-15 pointer-events-none">
                    <div className="absolute -right-40 top-20 w-[500px] h-[500px] bg-[#EC6A0A] rounded-full blur-3xl" />
                </div>
                <div className="ucs-container grid md:grid-cols-12 gap-12 items-center relative">
                    <div className="md:col-span-7">
                        <div className="inline-flex items-center gap-2 bg-[#EC6A0A]/15 text-[#EC6A0A] px-3 py-1.5 mb-5 text-xs tracking-[0.2em] font-bold uppercase">
                            Wij kopen uw wagen
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
                            Verkoop uw wagen — <span className="text-[#EC6A0A]">vrijblijvend bod</span><br />binnen 24 uur.
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">
                            Snel, eerlijk en zonder gedoe. Wij komen uw wagen kosteloos ophalen in heel België —
                            ook zonder aankoop bij ons. Reeds <strong>500+ wagens</strong> overgenomen.
                        </p>

                        <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
                            {[
                                "Eerlijke marktwaarde",
                                "Direct cash of overschrijving",
                                "Wij regelen alle papierwerk",
                                "Antwoord binnen 24 uur",
                            ].map((t, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="mt-0.5 w-5 h-5 bg-[#EC6A0A] flex-shrink-0 flex items-center justify-center text-[10px] font-black">✓</span>
                                    <span className="text-white/90">{t}</span>
                                </li>
                            ))}
                        </ul>

                        <Link to="/inruil#form" className="ucs-btn-accent mt-10 text-base py-4 px-7" data-testid="home-cta-inruil">
                            Vraag mijn bod aan <ArrowRight className="w-5 h-5" />
                        </Link>
                        <p className="mt-3 text-xs text-white/60">Geen verplichtingen · 100% gratis</p>
                    </div>
                    <div className="md:col-span-5 aspect-[4/3] bg-slate-100 overflow-hidden" style={{borderRadius: 4}}>
                        <img src="https://images.unsplash.com/photo-1644749700856-a82a92828a1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                             alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                </div>
            </section>

            {/* AANBOD */}
            <section className="py-24 bg-[#F8F9FA]">
                <div className="ucs-container">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="label-eyebrow mb-2">Recent toegevoegd</div>
                            <h2 className="text-3xl md:text-5xl font-black text-[#0E2C4D] tracking-tight">Het nieuwste aanbod</h2>
                        </div>
                        <Link to="/aanbod" className="text-[#0E2C4D] font-bold flex items-center gap-2 hover:text-[#EC6A0A] transition-colors" data-testid="home-view-all">
                            Bekijk alles <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-car-grid">
                        {cars.map((c) => <CarCard key={c.id} car={c} />)}
                    </div>
                </div>
            </section>

            <ReviewsSection />

            <Footer />
        </div>
    );
}

const Stat = ({ value, label }) => (
    <div className="bg-white/5 border border-white/10 backdrop-blur p-6">
        <div className="text-3xl font-black text-[#EC6A0A]">{value}</div>
        <div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2 font-bold">{label}</div>
    </div>
);

const USP = ({ icon, title, desc }) => (
    <div>
        <div className="w-12 h-12 bg-[#EC6A0A]/10 text-[#EC6A0A] flex items-center justify-center mb-5" style={{borderRadius: 2}}>
            {icon}
        </div>
        <h3 className="text-xl font-bold text-[#0E2C4D] mb-2">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
);
